import React, { useState } from 'react';
import { 
  TrendingUp, 
  Users, 
  Target, 
  BarChart3, 
  FileText, 
  Boxes, 
  Calculator, 
  Shield, 
  Factory, 
  Truck, 
  CheckCircle, 
  Receipt, 
  CreditCard, 
  BookOpen,
  ArrowLeft,
  Activity,
  Clock,
  DollarSign,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import { salesCycleDashboard } from '../../data/salesCycleData';

// Import the workflow components
import CustomerManagement from './CustomerManagement';
import LeadTracking from './LeadTracking';
import MarketAnalysis from './MarketAnalysis';
import PricingTools from './PricingTools';
import ContractManagement from './ContractManagement';
import CreditCheck from './CreditCheck';
import DeliveryManagement from './DeliveryManagement';
import InvoiceManagement from './InvoiceManagement';
import PaymentTracking from './PaymentTracking';

const SalesCycleDashboard = ({ onBack, onNavigate, navigationSource }) => {
  const [activeComponent, setActiveComponent] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getPhaseColor = (phase) => {
    const colors = {
      'A': 'from-blue-500 to-blue-600',
      'B': 'from-green-500 to-green-600',
      'C': 'from-purple-500 to-purple-600',
      'D': 'from-orange-500 to-orange-600',
      'E': 'from-red-500 to-red-600'
    };
    return colors[phase] || 'from-gray-500 to-gray-600';
  };

  const getModuleIcon = (moduleId) => {
    const icons = {
      'customer-management': Users,
      'lead-tracking': Target,
      'market-analysis': BarChart3,
      'quotation': FileText,
      'inventory': Boxes,
      'pricing-tools': Calculator,
      'sales-orders': TrendingUp,
      'contract-management': FileText,
      'credit-check': Shield,
      'production': Factory,
      'delivery': Truck,
      'quality-control': CheckCircle,
      'invoice': Receipt,
      'payment-tracking': CreditCard,
      'journal': BookOpen
    };
    return icons[moduleId] || Activity;
  };

  const handleModuleClick = (moduleId) => {
    switch (moduleId) {
      case 'customer-management':
        setActiveComponent('customer-management');
        break;
      case 'lead-tracking':
        setActiveComponent('lead-tracking');
        break;
      case 'market-analysis':
        setActiveComponent('market-analysis');
        break;
      case 'pricing-tools':
        setActiveComponent('pricing-tools');
        break;
      case 'quotation':
        onNavigate('quotation');
        break;
      case 'inventory':
        onNavigate('inventory');
        break;
      case 'sales-orders':
        onNavigate('sales-orders');
        break;
      case 'contract-management':
        setActiveComponent('contract-management');
        break;
      case 'credit-check':
        setActiveComponent('credit-check');
        break;
      case 'production':
        onNavigate('production');
        break;
      case 'delivery':
        setActiveComponent('delivery');
        break;
      case 'quality-control':
        onNavigate('quality-control');
        break;
      case 'invoice':
        setActiveComponent('invoice');
        break;
      case 'payment-tracking':
        setActiveComponent('payment-tracking');
        break;
      case 'journal':
        onNavigate('journal');
        break;
      default:
        console.log(`Module ${moduleId} not implemented yet`);
    }
  };

  const handleBackToDashboard = () => {
    setActiveComponent(null);
  };

  // If a component is active, render it
  if (activeComponent) {
    switch (activeComponent) {
      case 'customer-management':
        return <CustomerManagement onBack={handleBackToDashboard} navigationSource="sales-workflow" />;
      case 'lead-tracking':
        return <LeadTracking onBack={handleBackToDashboard} navigationSource="sales-workflow" />;
      case 'market-analysis':
        return <MarketAnalysis onBack={handleBackToDashboard} navigationSource="sales-workflow" />;
      case 'pricing-tools':
        return <PricingTools onBack={handleBackToDashboard} navigationSource="sales-workflow" />;
      case 'contract-management':
        return <ContractManagement onBack={handleBackToDashboard} navigationSource="sales-workflow" />;
      case 'credit-check':
        return <CreditCheck onBack={handleBackToDashboard} navigationSource="sales-workflow" />;
      case 'delivery':
        return <DeliveryManagement onBack={handleBackToDashboard} navigationSource="sales-workflow" />;
      case 'invoice':
        return <InvoiceManagement onBack={handleBackToDashboard} navigationSource="sales-workflow" />;
      case 'payment-tracking':
        return <PaymentTracking onBack={handleBackToDashboard} navigationSource="sales-workflow" />;
      default:
        return null;
    }
  }

  const salesWorkflow = [
    {
      id: 'A',
      phase: '潛客開發階段',
      title: '潛在客戶識別與開發',
      description: '透過各種管道識別潛在客戶，建立客戶檔案，分析客戶需求和購買潛力',
      duration: '3-5天',
      modules: [
        { 
          id: 'customer-management', 
          name: '客戶管理', 
          icon: 'Users', 
          required: true, 
          description: '建立完整客戶檔案和聯絡資訊',
          implemented: true
        },
        { 
          id: 'lead-tracking', 
          name: '潛客追蹤', 
          icon: 'Target', 
          required: true, 
          description: '管理潛在客戶開發進度',
          implemented: true
        },
        { 
          id: 'market-analysis', 
          name: '市場分析', 
          icon: 'BarChart3', 
          required: true, 
          description: '分析市場機會和競爭環境',
          implemented: true
        }
      ],
      deliverables: ['潛在客戶清單', '客戶需求分析', '市場機會評估', '客戶分群策略'],
      status: 'completed',
      completionRate: 100,
      keyTasks: ['蒐集客戶資訊', '分析客戶需求', '評估購買潛力', '建立聯絡計劃'],
      checkpoints: ['客戶資料完整', '需求分析確認', '開發計劃核准']
    },
    {
      id: 'B',
      phase: '報價提案階段',
      title: '產品報價與解決方案提案',
      description: '根據客戶需求準備產品報價，設計解決方案，提交正式提案文件',
      duration: '2-3天',
      modules: [
        { 
          id: 'quotation', 
          name: '報價單', 
          icon: 'FileText', 
          required: true, 
          description: '製作詳細產品報價單',
          implemented: true
        },
        { 
          id: 'inventory', 
          name: '庫存查詢', 
          icon: 'Boxes', 
          required: true, 
          description: '確認產品庫存和交期',
          implemented: true
        },
        { 
          id: 'pricing-tools', 
          name: '定價工具', 
          icon: 'Calculator', 
          required: true, 
          description: '計算最佳報價和利潤',
          implemented: true
        }
      ],
      deliverables: ['正式報價單', '產品規格文件', '交貨條件說明', '解決方案提案'],
      status: 'completed',
      completionRate: 100,
      keyTasks: ['準備產品資料', '計算報價成本', '確認庫存狀況', '製作提案文件'],
      checkpoints: ['報價核准', '庫存確認', '提案品質檢查']
    },
    {
      id: 'C',
      phase: '商務談判階段',
      title: '商務談判與銷售訂單確認',
      description: '與客戶進行商務談判，討論價格條件，最終確認銷售訂單和合約',
      duration: '3-7天',
      modules: [
        { 
          id: 'sales-orders', 
          name: '銷售訂單', 
          icon: 'TrendingUp', 
          required: true, 
          description: '建立和管理銷售訂單',
          implemented: true
        },
        { 
          id: 'contract-management', 
          name: '合約管理', 
          icon: 'FileText', 
          required: true, 
          description: '管理銷售合約和條款',
          implemented: true
        },
        { 
          id: 'credit-check', 
          name: '信用檢查', 
          icon: 'Shield', 
          required: true, 
          description: '評估客戶信用和付款能力',
          implemented: true
        }
      ],
      deliverables: ['銷售訂單', '簽署合約', '信用評估報告', '訂單核准記錄'],
      status: 'completed',
      completionRate: 100,
      keyTasks: ['進行商務談判', '準備銷售合約', '執行信用檢查', '取得訂單核准'],
      checkpoints: ['價格談判完成', '合約簽署', '信用核准通過']
    },
    {
      id: 'D',
      phase: '履約交付階段',
      title: '生產安排與產品交付',
      description: '根據銷售訂單安排生產，確保產品品質，按時交付給客戶',
      duration: '5-10天',
      modules: [
        { 
          id: 'production', 
          name: '生產管理', 
          icon: 'Factory', 
          required: true, 
          description: '安排產品生產和製造',
          implemented: true
        },
        { 
          id: 'delivery', 
          name: '交貨管理', 
          icon: 'Truck', 
          required: true, 
          description: '安排產品包裝和配送',
          implemented: true
        },
        { 
          id: 'quality-control', 
          name: '品質控制', 
          icon: 'CheckCircle', 
          required: true, 
          description: '確保產品品質符合標準',
          implemented: true
        }
      ],
      deliverables: ['生產排程計劃', '產品交貨單', '品質檢驗報告', '物流追蹤記錄'],
      status: 'completed',
      completionRate: 100,
      keyTasks: ['安排生產計劃', '執行品質檢驗', '準備產品包裝', '安排物流配送'],
      checkpoints: ['生產計劃確認', '品質檢驗通過', '準時交貨達成']
    },
    {
      id: 'E',
      phase: '收款結算階段',
      title: '發票開立與款項收取',
      description: '開立銷售發票，追蹤客戶付款，完成收款和帳務處理',
      duration: '1-30天',
      modules: [
        { 
          id: 'invoice', 
          name: '發票管理', 
          icon: 'Receipt', 
          required: true, 
          description: '開立正式銷售發票',
          implemented: true
        },
        { 
          id: 'payment-tracking', 
          name: '收款追蹤', 
          icon: 'CreditCard', 
          required: true, 
          description: '追蹤客戶付款進度',
          implemented: true
        },
        { 
          id: 'journal', 
          name: '會計記帳', 
          icon: 'BookOpen', 
          required: true, 
          description: '記錄收款和帳務處理',
          implemented: true
        }
      ],
      deliverables: ['銷售發票', '收款記錄', '應收帳款報表', '會計分錄'],
      status: 'completed',
      completionRate: 100,
      keyTasks: ['開立銷售發票', '寄送發票給客戶', '追蹤付款狀況', '記錄收款交易'],
      checkpoints: ['發票開立完成', '客戶確認收到', '款項收取完成']
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* 標題區域 */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">銷售週期管理</h1>
            <p className="text-gray-600 mt-2">從潛在客戶開發到收款完成的完整銷售流程管理</p>
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回業務週期</span>
            </button>
          )}
        </div>

        {/* 儀表板統計 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">客戶總數</p>
                <p className="text-2xl font-bold text-gray-800">{salesCycleDashboard.summary.total_customers}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">活躍潛客</p>
                <p className="text-2xl font-bold text-gray-800">{salesCycleDashboard.summary.active_leads}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Target className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">管道價值</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(salesCycleDashboard.summary.total_pipeline_value)}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">轉換率</p>
                <p className="text-2xl font-bold text-gray-800">{salesCycleDashboard.summary.conversion_rate}%</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <TrendingUp className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">平均週期</p>
                <p className="text-2xl font-bold text-gray-800">{salesCycleDashboard.summary.avg_sales_cycle}天</p>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <Clock className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* 銷售週期階段 */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-800">銷售週期階段</h2>
          
          {salesWorkflow.map((phase, index) => (
            <div key={phase.id} className="bg-white rounded-lg shadow overflow-hidden">
              {/* 階段標題 */}
              <div className={`bg-gradient-to-r ${getPhaseColor(phase.id)} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="bg-white bg-opacity-20 p-2 rounded-full">
                        <span className="text-xl font-bold">{phase.id}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">{phase.title}</h3>
                        <p className="text-sm opacity-90">{phase.phase}</p>
                      </div>
                    </div>
                    <p className="text-sm opacity-90">{phase.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold">{phase.completionRate}%</p>
                        <p className="text-xs opacity-75">完成率</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold">{phase.duration}</p>
                        <p className="text-xs opacity-75">預估時間</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 模組列表 */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {phase.modules.map((module) => {
                    const IconComponent = getModuleIcon(module.id);
                    return (
                      <div
                        key={module.id}
                        className={`border rounded-lg p-4 transition-all duration-200 ${
                          module.implemented 
                            ? 'border-gray-200 hover:border-blue-300 hover:shadow-md cursor-pointer' 
                            : 'border-gray-200 bg-gray-50 opacity-60'
                        }`}
                        onClick={() => module.implemented && handleModuleClick(module.id)}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className={`p-2 rounded-full ${
                            module.implemented ? 'bg-blue-100' : 'bg-gray-200'
                          }`}>
                            <IconComponent className={`w-5 h-5 ${
                              module.implemented ? 'text-blue-600' : 'text-gray-400'
                            }`} />
                          </div>
                          <div className="flex items-center space-x-2">
                            {module.required && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                                必要
                              </span>
                            )}
                            {module.implemented ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-yellow-600" />
                            )}
                          </div>
                        </div>
                        <h4 className={`font-semibold mb-2 ${
                          module.implemented ? 'text-gray-800' : 'text-gray-500'
                        }`}>
                          {module.name}
                        </h4>
                        <p className={`text-sm ${
                          module.implemented ? 'text-gray-600' : 'text-gray-400'
                        }`}>
                          {module.description}
                        </p>
                        {module.implemented && (
                          <div className="flex items-center justify-end mt-3">
                            <ChevronRight className="w-4 h-4 text-blue-600" />
                          </div>
                        )}
                        {!module.implemented && (
                          <div className="mt-3">
                            <span className="text-xs text-yellow-600 font-medium">開發中</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* 階段詳細資訊 */}
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2">主要任務</h5>
                    <ul className="space-y-1">
                      {phase.keyTasks.map((task, taskIndex) => (
                        <li key={taskIndex} className="flex items-center space-x-2 text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                          <span>{task}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2">檢查點</h5>
                    <ul className="space-y-1">
                      {phase.checkpoints.map((checkpoint, checkIndex) => (
                        <li key={checkIndex} className="flex items-center space-x-2 text-sm text-gray-600">
                          <CheckCircle className="w-3 h-3 text-green-600" />
                          <span>{checkpoint}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2">交付成果</h5>
                    <ul className="space-y-1">
                      {phase.deliverables.map((deliverable, delIndex) => (
                        <li key={delIndex} className="flex items-center space-x-2 text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full"></div>
                          <span>{deliverable}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 最新活動和機會 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">最新活動</h3>
            <div className="space-y-4">
              {salesCycleDashboard.recent_activities.map((activity, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`p-2 rounded-full ${
                    activity.type === 'new_lead' ? 'bg-blue-100' :
                    activity.type === 'proposal_sent' ? 'bg-green-100' :
                    'bg-purple-100'
                  }`}>
                    {activity.type === 'new_lead' && <Target className="w-4 h-4 text-blue-600" />}
                    {activity.type === 'proposal_sent' && <FileText className="w-4 h-4 text-green-600" />}
                    {activity.type === 'contract_signed' && <CheckCircle className="w-4 h-4 text-purple-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">{activity.description}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-gray-500">{activity.date}</p>
                      <p className="text-xs font-medium text-green-600">{formatCurrency(activity.value)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">重點機會</h3>
            <div className="space-y-4">
              {salesCycleDashboard.top_opportunities.map((opportunity, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-800">{opportunity.customer}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      opportunity.probability >= 80 ? 'bg-green-100 text-green-800' :
                      opportunity.probability >= 60 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {opportunity.probability}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>{formatCurrency(opportunity.value)}</span>
                    <span>{opportunity.close_date}</span>
                  </div>
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${opportunity.probability}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesCycleDashboard; 